import { CheckOutlined, DownOutlined } from "@ant-design/icons";
import { Button, Form, FormInstance, Input, Select } from "antd";
import { ethers } from "ethers";
import { useCallback, useEffect, useState } from "react";
import { useGetDetailWallet } from "../../../../../api/wallet";
import { buttonStyle } from "../../../../../components/pages/auth/components/styles";
import {
  addressWalletRules,
  clearFieldErrorMessageOnChange,
} from "../../../../../utils/form.constants";
import { useMetadataCurrencyToken } from "../../../../../utils/hooks/useMetadataCurrencyToken";
import { useMetadataNetwork } from "../../../../../utils/hooks/useMetadataNetwork";
import { getNetWorkByName, getSymbolByName } from "../../../../../utils/list.utils";

interface IFormWalletProps {
  form: FormInstance<any>;
  walletId: number | null;
  handleCancel: () => void;
  onSubmitWallet: (values: any) => void;
  isLoading: boolean;
  actionWallet: "ADD" | "EDIT" | null;
}

const requiredFields = ["name", "assetConfigGroupId", "networkId", "address"];

const FormWallet = ({
  form,
  walletId,
  handleCancel,
  onSubmitWallet,
  isLoading,
  actionWallet,
}: IFormWalletProps) => {
  const currency = Form.useWatch("assetConfigGroupId", form);
  const network = Form.useWatch("networkId", form);
  const address = Form.useWatch("address", form);

  const {
    data: detailWallet,
    refetch: refetchDetailWallet,
  } = useGetDetailWallet(walletId || 0);

  const { metadataCurrencyToken, dataCurrencyToken } = useMetadataCurrencyToken(
    Number(network || 0)
  );

  const { metadataNetwork, dataNetwork } = useMetadataNetwork(
    Number(currency || 0)
  );

  const [searchCurrencyToken, setSearchCurrencyToken] = useState<string>("");
  const [searchNetwork, setSearchNetwork] = useState<string>("");
  const [addressBlur, setAddressBlur] = useState(false);
  const [addressValue, setAddressValue] = useState<any>();
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false);

  useEffect(() => {
    if (!actionWallet) {
      form.resetFields();
      setSearchCurrencyToken("");
      setSearchNetwork("");
      setAddressBlur(false);
      setAddressValue(undefined);
      setIsAllFieldsFilled(false);
      return;
    }

    if (actionWallet === "ADD") {
      form.resetFields();
      setSearchCurrencyToken("");
      setSearchNetwork("");
      setAddressBlur(false);
      setAddressValue(undefined);
      setIsAllFieldsFilled(false);
      return;
    }

    if (actionWallet === "EDIT" && walletId) {
      form.resetFields();
      setAddressValue(undefined);
      refetchDetailWallet();
    }
  }, [actionWallet, form, refetchDetailWallet, walletId]);

  const handleChangeCurrencyToken = (value: string) => {
    setSearchCurrencyToken(value);
  };

  const handleChangeNetwork = (value: string) => {
    setSearchNetwork(value);
  };

  // Helper function to check if all required fields are filled
  const checkAllFieldsFilled = useCallback((addressOverride?: string) => {
    const values = form.getFieldsValue(requiredFields);
    const currentAddress = addressOverride !== undefined
      ? addressOverride
      : (addressValue !== undefined ? addressValue : address);

    const allFilled = requiredFields.every((field) => {
      const value = field === "address" ? currentAddress : values[field];
      return !!value;
    });
    setIsAllFieldsFilled(allFilled);
  }, [form, addressValue, address]);

  const handleFormValuesChange = (changedValues: any) => {
    // Clear field error messages
    clearFieldErrorMessageOnChange(form)(changedValues);

    // Sync addressValue when address field changes in form
    if (changedValues.address !== undefined) {
      setAddressValue(changedValues.address);
    }

    // Update isAllFieldsFilled immediately when form values change
    checkAllFieldsFilled();
  };

  const isValidAddressByNetwork = (
    address: string,
    network: number
  ): boolean => {
    if (!address) return false;
    switch (network) {
      case 1:
      case 2:
        return ethers.isAddress(address);
      case 3:
        // Basic Tron address validation: starts with 'T' and length >= 34
        return address.length >= 34 && address.startsWith('T');
      default:
        return false;
    }
  };

  useEffect(() => {
    if (!addressValue || !network || !addressBlur) return;
    form.validateFields(["address"]);
  }, [network, addressValue, addressBlur, form]);

  useEffect(() => {
    if (!walletId || !detailWallet) return;
    const detailId = detailWallet?.data?.data?.id;
    if (detailId !== undefined && Number(detailId) !== Number(walletId)) return;

    form.setFieldsValue({
      name: detailWallet.data.data.name || "",
      assetConfigGroupId: detailWallet.data.data.assetConfigGroupId || "",
      networkId: detailWallet.data.data.networkId || "",
      address: detailWallet.data.data.address || "",
    });
    setSearchNetwork(detailWallet.data.data.networkId || "");
    setSearchCurrencyToken(detailWallet.data.data.assetConfigGroupId || "");
    setAddressValue(detailWallet.data.data.address || "");
  }, [walletId, detailWallet, form]);

  const name = Form.useWatch("name", form);

  // Update isAllFieldsFilled when any required field changes
  useEffect(() => {
    checkAllFieldsFilled();
  }, [currency, network, address, addressValue, name, checkAllFieldsFilled]);

  useEffect(() => {
    if (
      !Array.isArray(metadataCurrencyToken) ||
      !Array.isArray(metadataNetwork)
    )
      return;
    const currencyLabel = getSymbolByName(dataCurrencyToken, currency);
    const networkLabel = getNetWorkByName(dataNetwork, network);
    if (currencyLabel && networkLabel && address && address.length >= 4) {
      const last4 = address.slice(-4);
      form.setFieldsValue({
        name: `${currencyLabel}-${networkLabel}-${last4}`,
      });
    } else {
      form.setFieldsValue({ name: "" });
    }
  }, [
    currency,
    network,
    address,
    form,
    metadataCurrencyToken,
    metadataNetwork,
    dataCurrencyToken,
    dataNetwork,
  ]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSubmitWallet}
      onValuesChange={handleFormValuesChange}
    >
      <Form.Item
        label="Name"
        name="name"
        validateTrigger="onBlur"
        rules={[
          { required: true, message: "This field is required", whitespace: true },
          {
            min: 3,
            message: "Minimum 3 characters",
            validateTrigger: "onBlur",
          },
          {
            max: 120,
            message: "Maximum 120 characters",
            validateTrigger: "onBlur",
          },
        ]}
      >
        <Input
          disabled
          placeholder="e.g. ETH-ERC20-2f74"
          style={buttonStyle}
          size="large"
        />
      </Form.Item>
      <Form.Item
        label="Crypto Currency"
        name="assetConfigGroupId"
        validateTrigger="onBlur"
        rules={[
          {
            required: true,
            message: "This field is required",
          },
        ]}
      >
        <Select
          placeholder="Select the crypto currency"
          suffixIcon={<DownOutlined />}
          onChange={handleChangeCurrencyToken}
          optionLabelProp="label"
          size="large"
          style={buttonStyle}
        >
          {metadataCurrencyToken.map((item: { value: string; label: string }) => (
            <Select.Option
              key={item.value}
              value={item.value}
              label={item.label}
            >
              <span
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 2,
                }}
              >
                <span
                  className="ant-select-custom-value"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {item.label}
                </span>
                {searchCurrencyToken === item.value && (
                  <CheckOutlined style={{ flexShrink: 0 }} />
                )}
              </span>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="Network"
        name="networkId"
        validateTrigger="onBlur"
        rules={[
          {
            required: true,
            message: "This field is required",
          },
        ]}
      >
        <Select
          placeholder="Select the blockchain network"
          suffixIcon={<DownOutlined />}
          onChange={handleChangeNetwork}
          optionLabelProp="label"
          size="large"
          style={buttonStyle}
        >
          {metadataNetwork.map((item: { value: string; label: string }) => (
            <Select.Option
              key={item.value}
              value={item.value}
              label={item.label}
            >
              <span
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 2,
                }}
              >
                <span
                  className="ant-select-custom-value"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {item.label}
                </span>
                {searchNetwork === item.value && (
                  <CheckOutlined style={{ flexShrink: 0 }} />
                )}
              </span>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        label="Address"
        name="address"
        validateTrigger="onBlur"
        rules={[
          ...addressWalletRules,
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              if (value && !network) return Promise.resolve();
              if (value && isValidAddressByNetwork(value, Number(network))) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error("Invalid address for selected network")
              );
            },
          },
        ]}
      >
        <Input
          placeholder="Enter the wallet address"
          onChange={(e) => {
            const newValue = e?.target.value;
            setAddressValue(newValue);
            // Immediately check if all fields are filled when address changes
            checkAllFieldsFilled(newValue);
          }}
          onBlur={() => setAddressBlur(true)}
          onFocus={() => setAddressBlur(false)}
          style={buttonStyle}
          size="large"
        />
      </Form.Item>
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
        <Button
          onClick={handleCancel}
          size="large"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          size="large"
          loading={isLoading}
          disabled={!isAllFieldsFilled}
        >
          Save
        </Button>
      </div>
    </Form>
  );
};

export default FormWallet;

